"use client";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { CrownIcon, SortIcon, Spinner } from "@soma/ui";
import { CopyButton } from "@/components/ui/CopyButton";
import { MineButton } from "@/components/ui/MineButton";
import { BAND_TONE } from "@/components/ui/scoreBand";
import { Tag, type TagTone } from "@/components/ui/Tag";
import { dashboardContent, fmt } from "@/lib/content";
import { categoryLabel } from "@/lib/dashboard/categories";
import { fmtScore, fmtSubmit, hotkeyHue, shortHotkey } from "@/lib/dashboard/format";
import type { LeaderboardRow, LeaderboardScales, StageCell } from "@/lib/dashboard/rows";
import { band, type Scale } from "@/lib/dashboard/scale";
import { isWorking } from "@/lib/dashboard/screener";
import { useMyHotkey } from "@/lib/hooks/useMyHotkey";
import { CATEGORY_SORT_PREFIX, type SortDirection, type SortKey } from "@/lib/dashboard/table";
import { cn } from "@/lib/utils";

const STATUS_TONE: Record<string, TagTone> = {
  scored: "ok",
  "not qualified": "accent",
  "failed review": "solid",
};

/**
 * Digits are set in the sans face with tabular figures, not in the mono one. Geist Mono
 * slashes its zero, which at this density turns a column of 0.045 / 0.050 / 0.067 into a
 * field of hatching, and it costs about a character of width per cell. Tabular figures
 * line up just as well. Hotkeys keep mono: base58 has no zero in its alphabet.
 */
const NUM = "font-sans tabular-nums";

/**
 * Every numeric cell reserves the width of the widest value its format can produce -
 * "-0.013", six tabular characters - whatever it actually holds. Without it the column
 * measures whatever the current filter happens to contain, so switching from All to
 * Scored (dashes to numbers) resizes half the table. Same reason for the fixed slots
 * around the crown and the pass mark, and for the invisible sizer in the status header.
 */
const SCORE_W = "inline-block min-w-[6ch] text-right";
const SUBMIT_W = "inline-block min-w-[15ch] text-right";
const MARK_W = "inline-flex w-3 shrink-0 justify-center";

/**
 * The table runs to the card's edges, so the card's own gutter lives in the outer cells
 * rather than on a wrapper. Anything that sets padding here sets all of it: cn() joins
 * classes without resolving conflicts, so pl-3 and pl-6 must never both be emitted.
 */
const EDGE_START = "pl-6 pr-3";
const EDGE_END = "pl-3 pr-6";

/**
 * Which columns survive which width. The table never scrolls sideways; it drops the
 * least load-bearing column instead, in the order someone reading a leaderboard gives
 * them up: identity, result and verdict at every size, then the breakdown by difficulty,
 * then when it landed, then the screener gates.
 *
 * The thresholds are measured, not picked off the breakpoint scale. The nine columns
 * need 876px of table, the seven without the screeners need 715, the six without the
 * timestamp need 555; adding back the page gutter, plus a little headroom, lands them
 * at 950, 785 and 610. (The pin beside each hotkey added 28px to every one of them.) Pinning these to lg and xl is what emptied the table on a 937px
 * window, which is where this started. Re-measure after anything that changes a cell's
 * width - the gutters moving into the outer cells for the full-bleed rules cost 48px.
 */
const CATEGORIES_AT = "hidden min-[610px]:table-cell";
const SUBMIT_AT = "hidden min-[785px]:table-cell";
const SCREENER_AT = "hidden min-[950px]:table-cell";

/** DOM id of a miner's row, so the "your miner" strip can scroll to it. */
export function rowId(hotkey: string): string {
  return `miner-${hotkey}`;
}

export function LeaderboardTable({
  rows,
  columns,
  scales,
  topHotkey,
  compId,
  sortKey,
  sortDirection,
  onSort,
}: {
  rows: LeaderboardRow[];
  columns: string[];
  scales: LeaderboardScales;
  topHotkey: string | null;
  compId: number | null;
  sortKey: SortKey;
  sortDirection: SortDirection;
  onSort: (key: SortKey) => void;
}) {
  const t = dashboardContent.leaderboard;
  const head = { sortKey, sortDirection, onSort };
  const router = useRouter();
  const mine = useMyHotkey().hotkey;
  const href = (hotkey: string) => (compId === null ? null : `/dashboard/miner/${compId}/${hotkey}`);

  /**
   * The whole row is the target; the hotkey stays a real link so the keyboard, the
   * status bar and middle-click keep working, and clicks that land on it are left alone.
   * A click that ends a text selection is someone reading a score, not navigating.
   */
  function openRow(event: React.MouseEvent, to: string | null) {
    if (!to) return;
    if ((event.target as HTMLElement).closest("a, button")) return;
    if (window.getSelection()?.toString()) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) {
      window.open(to, "_blank", "noopener");
      return;
    }
    router.push(to);
  }
  // The longest label any status can render, so the column is sized once and for all.
  const widestStatus = Object.values(t.statusLabels).reduce((a, b) => (b.length > a.length ? b : a), "");

  return (
    <table className="w-full border-collapse text-[12px]">
      <thead>
        <tr className="border-b border-line">
          <Th edge="start">{t.columns.hotkey}</Th>
          <Th sort="overall" {...head}>
            {t.columns.overall}
          </Th>
          <Th sort="stage1" className={SCREENER_AT} {...head}>
            {t.columns.stage1}
          </Th>
          <Th sort="stage2" className={SCREENER_AT} {...head}>
            {t.columns.stage2}
          </Th>
          {columns.map((c) => (
            <Th key={c} sort={`${CATEGORY_SORT_PREFIX}${c}`} className={CATEGORIES_AT} {...head}>
              {categoryLabel(c)}
            </Th>
          ))}
          <Th sort="submit" className={SUBMIT_AT} {...head}>
            {t.columns.submit}
          </Th>
          <Th sort="status" edge="end" sizer={<Tag tone="muted" size="xs">{widestStatus}</Tag>} {...head}>
            {t.columns.status}
          </Th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr
            key={row.hotkey}
            id={rowId(row.hotkey)}
            onClick={(e) => openRow(e, href(row.hotkey))}
            onAuxClick={(e) => e.button === 1 && openRow(e, href(row.hotkey))}
            className={cn(
              "group/row scroll-mt-40 border-b border-line-soft transition-colors",
              // The followed miner: a tint and a rule on the leading edge, the way the
              // sidebar marks the current page. The tint stays under the hover band.
              row.hotkey === mine ? "bg-accent-soft/50 shadow-[inset_2px_0_0_var(--color-accent)] hover:bg-accent-soft" : "hover:bg-bg-sunk",
              compId !== null && "cursor-pointer",
            )}
          >
            <td className={cn("w-full py-1.5", EDGE_START)}>
              <span className="flex items-center gap-1">
                <Link
                  href={href(row.hotkey) ?? "#"}
                  // One link per miner: prefetching every row in view is a burst of
                  // renders (and backend calls) nobody asked for.
                  prefetch={false}
                  className="flex items-center gap-2 transition-colors hover:text-accent"
                >
                  <span
                    aria-hidden
                    className="size-2 shrink-0 rounded-full"
                    style={{ background: `hsl(${hotkeyHue(row.hotkey)} 55% 55%)` }}
                  />
                  <span className="font-mono text-[12px] whitespace-nowrap sm:hidden">
                    {shortHotkey(row.hotkey, 4)}
                  </span>
                  <span className="hidden font-mono text-[12px] whitespace-nowrap sm:inline lg:hidden">
                    {shortHotkey(row.hotkey, 6)}
                  </span>
                  <span className="hidden font-mono text-[12px] whitespace-nowrap lg:inline">
                    {shortHotkey(row.hotkey)}
                  </span>
                </Link>
                <CopyButton
                  value={row.hotkey}
                  label={dashboardContent.miner.copyHotkey}
                  copiedLabel={dashboardContent.miner.copiedHotkey}
                  variant="bare"
                />
                <MineButton hotkey={row.hotkey} variant="bare" />
              </span>
            </td>

            <Cell tone={BAND_TONE[band(row.overall, scales.overall)]}>
              <span aria-hidden className={cn(MARK_W, "mr-1")}>
                {row.hotkey === topHotkey && <CrownIcon className="text-score-high" />}
              </span>
              {/* The crown is decoration to the eye; to a screen reader it is the fact itself. */}
              {row.hotkey === topHotkey && <span className="sr-only">{t.top}</span>}
              <span className={cn(SCORE_W, "font-medium")}>{fmtScore(row.overall)}</span>
            </Cell>

            <Stage cell={row.stage1} scale={scales.stage1} className={SCREENER_AT} />
            <Stage cell={row.stage2} scale={scales.stage2} className={SCREENER_AT} />

            {columns.map((c) => (
              <Cell
                key={c}
                className={CATEGORIES_AT}
                tone={BAND_TONE[band(row.categories[c] ?? null, scales.categories[c] ?? null)]}
              >
                <span className={SCORE_W}>{fmtScore(row.categories[c] ?? null)}</span>
              </Cell>
            ))}

            <Cell className={SUBMIT_AT} tone="text-fg-muted">
              <span className={SUBMIT_W}>{fmtSubmit(row.lastSubmit)}</span>
            </Cell>

            <td className={cn("py-2.5 text-right", EDGE_END)}>
              <Tag tone={STATUS_TONE[row.status ?? ""] ?? "muted"} size="xs" busy={isWorking(row.status)}>
                {t.statusLabels[row.status ?? "unknown"] ?? row.status}
              </Tag>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * A column heading. The hotkey names a thing and reads from the left; every other column
 * holds a figure, and figures are compared down their last digit, so both the heading and
 * the values align right with the sort mark on the outside.
 */
function Th({
  children,
  sort,
  className,
  edge,
  sizer,
  sortKey,
  sortDirection,
  onSort,
}: {
  children: React.ReactNode;
  sort?: SortKey;
  className?: string;
  /** First or last column: carries the card's gutter on its outer side. */
  edge?: "start" | "end";
  /** Rendered with no height, purely to hold the column open. */
  sizer?: React.ReactNode;
  sortKey?: SortKey;
  sortDirection?: SortDirection;
  onSort?: (key: SortKey) => void;
}) {
  const active = sort !== undefined && sort === sortKey;
  const label = typeof children === "string" ? children : "";
  const numeric = sort !== undefined;

  return (
    <th
      scope="col"
      aria-sort={active ? (sortDirection === "asc" ? "ascending" : "descending") : undefined}
      className={cn(
        "pb-2.5 text-[11.5px] font-normal whitespace-nowrap text-fg-muted",
        numeric ? "text-right" : "w-full text-left",
        edge === "start" ? EDGE_START : edge === "end" ? EDGE_END : numeric ? "pl-3" : "pr-3",
        className,
      )}
    >
      <span className={cn("inline-flex flex-col", numeric ? "items-end" : "items-start")}>
        {sort && onSort ? (
          <button
            type="button"
            onClick={() => onSort(sort)}
            aria-label={fmt(dashboardContent.leaderboard.sortAria, { column: label })}
            className={cn(
              "inline-flex cursor-pointer items-center gap-0.5 transition-colors hover:text-fg",
              active && "text-fg",
            )}
          >
            {children}
            <SortIcon
              direction={active ? (sortDirection ?? null) : null}
              className={cn("shrink-0", active ? "opacity-100" : "opacity-35")}
            />
          </button>
        ) : (
          children
        )}
        {sizer && (
          <span aria-hidden className="pointer-events-none h-0 overflow-hidden">
            {sizer}
          </span>
        )}
      </span>
    </th>
  );
}

function Cell({
  children,
  tone,
  className,
}: {
  children: React.ReactNode;
  tone: string;
  className?: string;
}) {
  return (
    <td className={cn("py-2.5 pl-3 text-right whitespace-nowrap", NUM, tone, className)}>
      <span className="inline-flex items-center justify-end">{children}</span>
    </td>
  );
}

/**
 * Score plus the stage's mark. A stage still being measured gets a spinner rather than a
 * tick: the backend flags stage 2 as passed the moment a miner qualifies, well before it
 * has a score, and a tick there claims a result the row cannot show.
 */
function Stage({ cell, scale, className }: { cell: StageCell; scale: Scale | null; className?: string }) {
  return (
    <Cell className={className} tone={BAND_TONE[band(cell.score, scale)]}>
      <span className={SCORE_W}>{fmtScore(cell.score)}</span>
      <span aria-hidden className={cn(MARK_W, "ml-1")}>
        {cell.verdict === "passed" && <span className="text-ok">✓</span>}
        {cell.verdict === "failed" && <span className="text-accent">✗</span>}
        {cell.verdict === "running" && <Spinner className="size-2.5 text-fg-faint" />}
      </span>
    </Cell>
  );
}
